import { formatSizeLabel } from './sizeOptions';
import { VariantSummaryMaterial, VariantSummary } from '../components/interfaces';
import { getVariantStockQuantity, isVariantAvailable } from './variantInventory';

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

export const formatVariantPrice = (price?: number | null) =>
  currencyFormatter.format(Number(price || 0));

export const getVariantSummary = ({
  material,
  selectedSize,
  productId,
  requiresSize,
  isLoading = false,
}: {
  material?: VariantSummaryMaterial | null;
  selectedSize?: string | null;
  productId?: string | null;
  requiresSize: boolean;
  isLoading?: boolean;
}): VariantSummary => {
  const price = Number(material?.price || 0);
  const stockQuantity = material
    ? getVariantStockQuantity(material, selectedSize, productId)
    : null;
  const available = Boolean(material) && isVariantAvailable(material, selectedSize, productId);
  const hasRequiredSize = !requiresSize || Boolean(selectedSize);
  const isOutOfStock = typeof stockQuantity === 'number' && stockQuantity <= 0;
  const canAddToCart = !isLoading && available && hasRequiredSize && !isOutOfStock && price > 0;
  const materialName = material?.materialName || 'Selected material';
  const selectedVariantLabel = selectedSize
    ? `${materialName} / ${formatSizeLabel(selectedSize)}`
    : materialName;

  if (isLoading) {
    return {
      price,
      stockQuantity: null,
      isAvailable: false,
      isOutOfStock: false,
      canAddToCart: false,
      statusLabel: 'Loading variant',
      statusTone: 'neutral',
      selectedVariantLabel,
    };
  }

  if (!material) {
    return {
      price,
      stockQuantity: null,
      isAvailable: false,
      isOutOfStock: false,
      canAddToCart: false,
      statusLabel: 'Select a material',
      statusTone: 'warning',
      selectedVariantLabel,
    };
  }

  if (!hasRequiredSize) {
    return {
      price,
      stockQuantity: typeof stockQuantity === 'number' ? stockQuantity : null,
      isAvailable: available,
      isOutOfStock,
      canAddToCart: false,
      statusLabel: 'Select a size',
      statusTone: 'warning',
      selectedVariantLabel,
    };
  }

  if (!available) {
    return {
      price,
      stockQuantity: typeof stockQuantity === 'number' ? stockQuantity : null,
      isAvailable: false,
      isOutOfStock,
      canAddToCart: false,
      statusLabel: 'Unavailable',
      statusTone: 'danger',
      selectedVariantLabel,
    };
  }

  if (isOutOfStock) {
    return {
      price,
      stockQuantity: 0,
      isAvailable: true,
      isOutOfStock: true,
      canAddToCart: false,
      statusLabel: 'Out of stock',
      statusTone: 'danger',
      selectedVariantLabel,
    };
  }

  return {
    price,
    stockQuantity: typeof stockQuantity === 'number' ? stockQuantity : null,
    isAvailable: true,
    isOutOfStock: false,
    canAddToCart,
    statusLabel: typeof stockQuantity === 'number' ? `${stockQuantity} in stock` : 'In stock',
    statusTone: typeof stockQuantity === 'number' && stockQuantity <= 2 ? 'warning' : 'success',
    selectedVariantLabel,
  };
};
